import { Collapse } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Balancer } from 'react-wrap-balancer';
import d from 'dayjs';
import { FaPhone } from 'react-icons/fa6';
import { MdEmail } from 'react-icons/md';
import api from 'src/api';
import Spinner from './spinner';

const iconStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '30px',
  width: '30px',
  borderRadius: '100vw',
  backgroundColor: '#f1f1f1'
};

/** Booking, guest and user details for a calendar entry that carries a booking. */
function BookingDetailsCard({ entry }) {
  const bookingId = entry?.bookingId?._id || entry?.bookingId;

  const {
    data: booking,
    error: bookingError,
    isLoading: isBookingInfoLoading
  } = useQuery({
    queryKey: ['booking', bookingId],
    enabled: !!bookingId,
    staleTime: 0,
    queryFn: async () => {
      const { data: { booking = {} } = {} } = await api.get(
        `/booking/one/${bookingId}`
      );
      return booking;
    }
  });

  const {
    data: user,
    error: userError,
    isLoading: isUserLoading
  } = useQuery({
    queryKey: ['user-details', booking?.userId, booking?.guestList],
    enabled: !!booking?.userId,
    queryFn: async () => {
      const { data: { user = {} } = {} } = await api.get(
        `/user/basic-details/${booking?.userId}${
          booking?.guestList?.length
            ? `/${booking?.guestList?.join?.(' ')}`
            : ''
        }`
      );
      return user;
    }
  });

  const pkgDetails = booking?.pkgDetails ? JSON.parse(booking.pkgDetails) : null;
  const diff = d(booking?.checkOutDate).diff(d(booking?.checkInDate), 'day');

  const guestsInfo = user?.guests?.map(guest => ({
    key: guest?._id,
    label: guest?.firstName + ' ' + guest?.lastName,
    children: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={iconStyles}>
            <FaPhone />
          </div>
          <p>{guest?.phone}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={iconStyles}>
            <MdEmail />
          </div>
          <p>{guest?.email}</p>
        </div>
      </div>
    )
  }));

  if (!bookingId) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {isBookingInfoLoading ? (
        <Spinner />
      ) : bookingError ? (
        <p style={{ color: 'red' }}>Error fetching booking details</p>
      ) : (
        <div>
          <h4 style={{ color: 'var(--main-brand-color)' }}>Booking Details</h4>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              marginTop: '5px'
            }}
          >
            <h5>Check In</h5>
            <h5>Check Out</h5>
            <p>{d(booking?.checkInDate).format('DD MMM YYYY')}</p>
            <p>{d(booking?.checkOutDate).format('DD MMM YYYY')}</p>
          </div>
          {pkgDetails && (
            <>
              <h5 style={{ marginTop: '5px' }}>Details</h5>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <h6>No. of Adults</h6>
                <h6>Paid Amount</h6>
                <p>{pkgDetails.noOfAdults}</p>
                <p>{pkgDetails.discountedAmount}</p>
                <h6>Status</h6>
                <h6>Length Of Stay</h6>
                <p style={{ color: '#2a9d95' }}>{booking?.status}</p>
                <p>
                  {diff} {diff > 1 ? 'days' : 'day'}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {userError ? (
        <p>Error fetching user details</p>
      ) : isUserLoading ? (
        <Spinner />
      ) : (
        booking?.userId && (
          <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
            <h4 style={{ color: 'var(--main-brand-color)' }}>User Details</h4>
            <h5>Booked By</h5>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  ...iconStyles,
                  backgroundImage: `url("${process.env.REACT_APP_SERVER_URL}/images/${user?.profilePic}")`
                }}
              >
                {!user?.profilePic && (
                  <h4>
                    {user?.fName?.charAt?.(0)}
                    {user?.lName?.charAt?.(0)}
                  </h4>
                )}
              </div>
              <h5 style={{ textTransform: 'capitalize' }}>
                <Balancer>
                  {user?.fName} {user?.lName}
                </Balancer>
              </h5>
            </div>
            {user?.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={iconStyles}>
                  <FaPhone />
                </div>
                <p>
                  {user?.countryCode} {user?.phone}
                </p>
              </div>
            )}
            {user?.email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={iconStyles}>
                  <MdEmail size={20} />
                </div>
                <p style={{ wordBreak: 'break-word' }}>
                  <Balancer>{user?.email}</Balancer>
                </p>
              </div>
            )}
          </div>
        )
      )}

      {!!guestsInfo?.length && (
        <div>
          <h4 style={{ marginBottom: '10px', color: 'var(--main-brand-color)' }}>
            Guests Info
          </h4>
          <Collapse items={guestsInfo} defaultActiveKey={[guestsInfo?.[0]?.key]} />
        </div>
      )}
    </div>
  );
}

export default BookingDetailsCard;
